import { FunctionComponent, memo, useContext } from "react";
import { Tooltip } from "@edgetier/components";
import InteractionIcon from "components-for/interactions/interaction-icon";
import InteractionSummary from "components-for/interactions/interaction-summary";
import usePhraseMessageTranslation from "hooks-for/translations/use-phrase-message-translation";
import TranslationButton from "components-for/translations/translation-button";
import { IProps } from "./interaction-title.types";
import { InteractionContext } from "../interaction-context/interaction-context";
import "./interaction-title.scss";

/**
 * The title of the interaction modal. A tooltip showing the full title text is shown if not all the text is visible.
 * @param props.contentCleaned      The text of the title.
 * @param props.interactionType     Interaction type.
 * @param props.subject             The text of the title.
 * @param props.languageIsoCode     Interaction language iso code.
 * @param props.interactionId
 */
const InteractionTitle: FunctionComponent<IProps> = ({
    contentCleaned,
    interactionType,
    subject,
    languageIsoCode,
    interactionId,
}) => {
    const isMissingContentCleaned = typeof contentCleaned !== "string" || contentCleaned.trim().length === 0;
    const isMissingSubject = typeof subject !== "string" || subject.trim().length === 0;
    const { isTranslationActive, setIsTranslationActive } = useContext(InteractionContext);

    const { activeTranslations, toggleTranslation, isTranslationEnabled, userLanguage, languagesByIsoCode } =
        usePhraseMessageTranslation();

    return (
        <div className="interaction-title">
            <div className="interaction-title__interaction-type">
                <InteractionIcon interactionType={interactionType} />
            </div>

            {isTranslationEnabled &&
                typeof languageIsoCode === "string" &&
                typeof userLanguage !== "undefined" &&
                userLanguage.languageIsoCode !== languageIsoCode && (
                    <TranslationButton
                        isTranslated={
                            typeof activeTranslations[interactionId] !== "undefined"
                                ? activeTranslations[interactionId].isTranslated || isTranslationActive
                                : false
                        }
                        onClick={() => {
                            toggleTranslation(interactionId, subject ?? contentCleaned, languageIsoCode);
                            setIsTranslationActive(() => !isTranslationActive);
                        }}
                        textLanguage={languagesByIsoCode[languageIsoCode]}
                        userLanguage={userLanguage}
                    />
                )}

            <Tooltip
                content={
                    <div className="interaction-title__tooltip-content">
                        <InteractionSummary
                            contentCleaned={contentCleaned}
                            subject={subject}
                            activeTranslation={activeTranslations[interactionId]}
                        />
                    </div>
                }
                delayEnter={250}
                disableTooltip={
                    (isMissingContentCleaned && isMissingSubject) ||
                    (typeof activeTranslations[interactionId] !== "undefined" &&
                        activeTranslations[interactionId].isLoading)
                }
                useArrow
            >
                <h2>
                    <InteractionSummary
                        contentCleaned={contentCleaned}
                        subject={subject}
                        activeTranslation={activeTranslations[interactionId]}
                    />
                </h2>
            </Tooltip>
        </div>
    );
};

export default memo(InteractionTitle);
